// @flow

import React, { type Node, type ComponentType } from 'react';
import { useIncomingPayment, getCountry } from '@tectonics/prs/incoming-payment';
import { type Country } from '@tectonics/prs/country';
import { type PaymentMethodCode } from '@tectonics/prs/payment-method';
import { DisplayCardBrands } from './display-card-brands';
import { DisplayBoletoBrand } from './display-boleto/display-boleto-brand';
import { DisplayOneClickBrand } from './display-one-click';
import { DisplayBankTransferBrand, DisplayBankTransferBrandAfrica } from './display-bank-transfer';
import { DisplayBankBrands } from './display-tef-brands';
import { DisplayOXXOBrand } from './display-oxxo-brand';
import { DisplayOXXOPayBrand } from './display-oxxopay-brand';
import { DisplayPaynetBrands } from './display-paynet-brands';
import { DisplayGooglePayBrand } from './display-google-pay';
import { DisplaySpeiBrand } from './display-spei-brand';
import { DisplayEftBrand } from './display-eft';
import { DisplayKlapBrand } from './display-klap-brand';
import { DisplaySencillitoBrand } from './display-sencillito-brand';
import { DisplayServipagBrand } from './display-servipag-brand';
import { DisplayPagoefectivoBrand } from './display-pagoefectivo-brand';
import { DisplayDebitcardBrands } from './display-debitcard-brands';
import { DisplayVouchercardBrands } from './display-vouchercard-brands';
import { DisplayVRMultibeneficiosBrand } from './display-vrmultibeneficios-brand';
import {
  DisplaySafetypayBrand,
  DisplaySafetypayCashBrand,
  DisplaySafetypayOnlineBrand,
} from './display-safetypay-brand';
import { DisplayArgentineCashBrands } from './display-argentine-cash-brand';
import { DisplayWebpayBrands } from './display-webpay-brands';
import {
  DisplayEwalletBrands,
  DisplayColombianEWalletBrands,
  DisplayArgentineEwalletBrands,
  DisplayMexicanEwalletBrands,
  DisplayChileanEwalletBrands,
  DisplayNigerianEwalletBrands,
  DisplayIndiaEwalletBrands,
  DisplayMobileMoneyBrands,
} from './display-ewallet-brands';
import { DisplayEfectyBrand } from './display-efecty';
import { DisplayPixBrand } from './display-pix';
import { DisplayOzowBrand } from './display-ozow';
import { DisplayGuatemalanCashBrands } from './display-guatemalan-cash-brand';
import { DisplayCostaRicanCashBrands } from './display-costa-rican-cash-brand';
import { DisplayPanamanianCashBrands } from './display-panamanian-cash-brand';
import { DisplayElSalvadorianCashBrands } from './display-el-salvadorian-cash-brand';
import { DisplayTransferenciaElectronicaBrand } from './display-colombian-externalpayprovider-brand';
import { DisplayNupayBrand } from './display-nupay';
import { DisplayMpcreditoBrand } from './display-mpcredito';
import { DisplayInfiniaBrand } from './display-infinia-brand';
import { DisplayFawryBrand } from './display-fawry';

import { DisplayAplazoBrand } from './display-aplazo';
import { DisplayOpayBrand } from './display-opay';

type Props = {|
  paymentMethodCode: PaymentMethodCode,
|};

export function DisplayBrands({ paymentMethodCode }: Props): Node {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);
  const DisplayBrandsImplementation = getDisplayBrandsImplementation(paymentMethodCode, country);

  return (
    <DisplayBrandsImplementation />
  );
}

function getDisplayBrandsImplementation(paymentMethodCode: PaymentMethodCode, country: Country): ComponentType<*> {
  switch (paymentMethodCode) {
    case 'creditcard':
      return DisplayCardBrands;

    case 'oneclick':
      return DisplayOneClickBrand;

    case 'boleto':
      return DisplayBoletoBrand;

    case 'banktransfer':
    case 'nibbs':
      return resolveBankTransferBrand(country);

    case 'tef':
      return DisplayBankBrands;

    case 'oxxo':
      return DisplayOXXOBrand;

    case 'oxxopay':
      return DisplayOXXOPayBrand;

    case 'cash':
      return resolveCashBrands(country);

    case 'google':
      return DisplayGooglePayBrand;

    case 'spei':
      return DisplaySpeiBrand;

    case 'eft':
      return DisplayEftBrand;

    case 'multicaja':
      return DisplayKlapBrand;

    case 'webpay':
      return DisplayWebpayBrands;

    case 'sencillito':
      return DisplaySencillitoBrand;

    case 'servipag':
      return DisplayServipagBrand;

    case 'pagoefectivo':
      return DisplayPagoefectivoBrand;

    case 'safetypay':
      return DisplaySafetypayBrand;

    case 'safetypay-cash':
      return DisplaySafetypayCashBrand;

    case 'safetypay-online':
      return DisplaySafetypayOnlineBrand;

    case 'debitcard':
      return resolveDebitcardBrands(country);

    case 'ewallet':
      return resolveEwalletBrands(country);

    case 'pix':
      return DisplayPixBrand;

    case 'externalpayprovider':
      return resolveExternalPayProviderBrands(country);

    case 'vouchercard':
      return DisplayVouchercardBrands;

    case 'multibeneficios':
      return DisplayVRMultibeneficiosBrand;

    case 'nupay':
      return DisplayNupayBrand;

    case 'mpcredito':
      return DisplayMpcreditoBrand;

    default:
      throw new Error(`Display brands: there is no brands display for "${paymentMethodCode}".`);
  }
}

function resolveCashBrands(country: Country): ComponentType<*> {
  switch (country) {
    case 'ar':
      return DisplayArgentineCashBrands;

    case 'mx':
      return DisplayPaynetBrands;

    case 'co':
      return DisplayEfectyBrand;

    case 'gt':
      return DisplayGuatemalanCashBrands;

    case 'cr':
      return DisplayCostaRicanCashBrands;

    case 'pa':
      return DisplayPanamanianCashBrands;

    case 'sv':
      return DisplayElSalvadorianCashBrands;

    default:
      throw new Error(`DisplayBrands - resolveCashBrands - there is no cash brands implementation for country "${country}".`);
  }
}

function resolveBankTransferBrand(country: Country): ComponentType<*> {
  switch (country) {
    case 'ng':
    case 'za':
    case 'ke':
      return DisplayBankTransferBrandAfrica;
    case 'cl':
    case 'pe':
      return DisplayInfiniaBrand;

    default:
      return DisplayBankTransferBrand;
  }
}

function resolveEwalletBrands(country: Country): ComponentType<*> {
  switch (country) {
    case 'br':
      return DisplayEwalletBrands;

    case 'co':
      return DisplayColombianEWalletBrands;

    case 'ar':
      return DisplayArgentineEwalletBrands;

    case 'mx':
      return DisplayMexicanEwalletBrands;

    case 'cl':
      return DisplayChileanEwalletBrands;

    case 'ng':
      return DisplayNigerianEwalletBrands;

    case 'in':
      return DisplayIndiaEwalletBrands;

    case 'ke':
    case 'tz':
    case 'ug':
    case 'zm':
    case 'gh':
      return DisplayMobileMoneyBrands;

    default:
      throw new Error(`DisplayBrands - resolveEwalletBrands - there is no ewallet brands implementation for country "${country}".`);
  }
}

function resolveExternalPayProviderBrands(country: Country): ComponentType<*> {
  switch (country) {
    case 'eg':
      return DisplayFawryBrand;
    case 'za':
      return DisplayOzowBrand;
    case 'co':
      return DisplayTransferenciaElectronicaBrand;
    case 'mx':
      return DisplayAplazoBrand;
    case 'ng':
      return DisplayOpayBrand;
    default:
      throw new Error(`DisplayBrands - resolveExternalPayProviderBrands - there is no externalpayprovider brands implementation for country "${country}".`);
  }
}

function resolveDebitcardBrands(country: Country): ComponentType<*> {
  switch (country) {
    case 'br':
    case 'uy':
    case 'ar':
    case 'pa':
    case 'gt':
    case 'sv':
    case 'do':
    case 'cr':
    case 'py':
    case 'ke':
    case 'ng':
    case 'za':
    case 'co':
    case 'eg':
    case 'ec':
    case 'in':
    case 'jm':
      return DisplayDebitcardBrands;

    case 'mx':
      return DisplayCardBrands;

    default:
      throw new Error(`DisplayBrands - resolveDebitcardBrands - there is no debitcard brands implementation for country "${country}".`);
  }
}
